import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BUDGET, OVER, TOLERANCE } from "./consts";
import { images } from "@/assets";
import { useGetToleranceReport } from "@/services";
import { BUDGET_YEAR, cn, formatAmount, POSTING_YEAR } from "@/lib/utils";
import { Badge, Button } from "@/components/ui";
import { useLocation, useNavigate } from "react-router-dom";

const useToleranceReport = () => {
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const [filter, setFilter] = useState<any>([BUDGET, TOLERANCE, OVER]);
  const [openBomDialog, setOpenBomDialog] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedOption, setSelectedOption] = useState<string>(BUDGET_YEAR);
  const navigate = useNavigate();

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const breadcrumbDataActivity = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            Tolerance 360 Report (Year - {currentFinanceYear})
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbDataDashboard = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.dashboard}`,
        name: `Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            Tolerance 360 Report (Year - {currentFinanceYear})
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataActivity;

  const [summaryCards, setSummaryCards] = useState([
    {
      key: BUDGET,
      title: "Expense Within Budget",
      icon: images.doc_check_green,
      className: "bg-green-600",
      noGl: 13,
      amount: 5000,
    },
    {
      key: TOLERANCE,
      title: "Expense Within Budget + Tolerance",
      icon: images.doc_check_yellow,
      className: "bg-yellow-600",
      noGl: 13,
      amount: 5000,
    },
    {
      key: OVER,
      title: "Overshoot Budget",
      icon: images.doc_check_red,
      className: "bg-red-600",
      noGl: 13,
      amount: 5000,
    },
  ]);

  const { data: toleranceData, isLoading: isLoadingTolerance, refetch: refetchToleranceData, isRefetching: isRefetchingToleranceData } =
    useGetToleranceReport({
      communityId: communityDetail?.communityId as string,
      filter: {...(selectedOption == POSTING_YEAR ? {[POSTING_YEAR]: currentFinanceYear} : {[BUDGET_YEAR]: currentFinanceYear})},
      queryParams: {
        enabled: !!(communityDetail?.communityId && currentFinanceYear),
      },
    });

  const onSummaryCardClick = (value: string) => {
    const _filter = [...filter];
    if (_filter.includes(value)) {
      _filter.splice(_filter.indexOf(value), 1);
    } else {
      _filter.push(value);
    }

    setFilter(_filter);
  };

  useEffect(() => {
    if (!toleranceData?.data?.reportData) return;

    const {
      withinBudgetCount,
      withinBudgetAmt,
      withinBudgetTCount,
      withinBudgetTAmt,
      overBudgetCount,
      overBudgetAmt,
    } = toleranceData.data.reportData.reduce(
      (acc: any, item: any) => {
        const {
          approvedToDate = 0,
          budgetYearlyAmt = 0,
          actualToleranceAmt = 0,
        } = item;

        if (approvedToDate <= budgetYearlyAmt) {
          acc.withinBudgetCount += 1;
          acc.withinBudgetAmt += approvedToDate;
        } else if (approvedToDate <= budgetYearlyAmt + actualToleranceAmt) {
          acc.withinBudgetTCount += 1;
          acc.withinBudgetTAmt += approvedToDate;
        } else {
          acc.overBudgetCount += 1;
          acc.overBudgetAmt += approvedToDate;
        }

        return acc;
      },
      {
        withinBudgetCount: 0,
        withinBudgetAmt: 0,
        withinBudgetTCount: 0,
        withinBudgetTAmt: 0,
        overBudgetCount: 0,
        overBudgetAmt: 0,
      }
    );

    setSummaryCards((prev: any) => [
      { ...prev[0], noGl: withinBudgetCount, amount: withinBudgetAmt },
      { ...prev[1], noGl: withinBudgetTCount, amount: withinBudgetTAmt },
      { ...prev[2], noGl: overBudgetCount, amount: overBudgetAmt },
      // ...prev.slice(3), // keep rest unchanged
    ]);
  }, [toleranceData]);

  const totalExpense = useMemo(
    () => summaryCards?.reduce((acc, { amount = 0 }) => acc + amount, 0),
    [summaryCards]
  );

  const columns = [
    {
      accessorKey: "glMasterId",
      header: <div>GL Master</div>,
      cell: (row: any) => {
        return (
          <Button
            variant="link"
            className="p-0"
            onClick={() => {
              navigate(
                `${routes.budgetMgmt}/${row?.row?.original?.budgetId}/${routes.lineItems}`
              );
            }}
          >{`${row?.row?.original?.accountNumber || ''} - ${row?.row?.original?.accountShortDesc}`}</Button>
        );
      },
    },
    {
      accessorKey: "budgetYearlyAmt",
      meta: { align: "right" },
      header: (
        <div>
          Budget Before <br /> Tolerance
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.budgetYearlyAmt || "0"),
    },
    {
      accessorKey: "approvedToDate",
      meta: { align: "right" },
      header: (
        <div>
          Expense <br /> Incurred
        </div>
      ),
      cell: (row: any) => {
        const { approvedToDate, budgetYearlyAmt, actualToleranceAmt } =
          row?.row?.original;
        return (
          <Badge
            className={cn(
              "px-3 py-1 rounded-sm",
              approvedToDate <= budgetYearlyAmt
                ? "bg-green-100 text-green-600"
                : approvedToDate > budgetYearlyAmt &&
                  approvedToDate <= budgetYearlyAmt + actualToleranceAmt
                ? "bg-yellow-50 text-yellow-600"
                : "bg-red-50 text-red-600"
            )}
          >
            {formatAmount(row?.row?.original?.approvedToDate || "0")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "approvedToDate",
      meta: { align: "right" },
      header: (
        <div>
          Variance <br />
          <span className="text-gray-400">(Col 1 - Col 2)</span>
        </div>
      ),
      accessorFn: (row: any) =>
        formatAmount(
          Number(row?.budgetYearlyAmt) - Number(row?.approvedToDate)
        ),
    },
    {
      accessorKey: "budgetToleranceAmt",
      meta: { align: "right" },
      header: (
        <div>
          Budgeted <br />
          Tolerance
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.budgetToleranceAmt || "0"),
    },
    {
      accessorKey: "actualToleranceAmt",
      meta: { align: "right" },
      header: (
        <div>
          Actual <br /> Tolerance
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.actualToleranceAmt || "0"),
    },
    {
      accessorKey: "camApproval",
      header: (
        <div>
          CAM <br />
          Approval{" "}
          <span className="text-gray-300">
            (
            {formatAmount(
              toleranceData?.data?.annualApprovalLimits?.cam || "0"
            )}
            )
          </span>
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.camApproval || "0"),
    },
    {
      accessorKey: "bomApproval",
      header: (
        <div>
          BOM <br />
          Approval{" "}
          <span className="text-gray-300">
            (
            {formatAmount(
              toleranceData?.data?.annualApprovalLimits?.bom || "0"
            )}
            )
          </span>
        </div>
      ),
      cell: (row: any) => {
        if (row?.row?.original?.bomApproval) {
          return (
            <Button
              variant="link"
              className="p-0"
              onClick={() => {
                setOpenBomDialog(true);
                setSelectedRow(row?.row?.original);
              }}
            >
              {formatAmount(row?.row?.original?.bomApproval || "0")}
            </Button>
          );
        } else {
          return formatAmount(row?.row?.original?.bomApproval || "0");
        }
      },
    },
    {
      accessorKey: "boardApproval",
      header: (
        <div>
          Board <br />
          Approval{" "}
          <span className="text-gray-300">
            (
            {formatAmount(
              toleranceData?.data?.annualApprovalLimits?.board || "0"
            )}
            )
          </span>
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.boardApproval || "0"),
    },
    {
      accessorKey: "agmApproval",
      header: (
        <div>
          AGM <br />
          Approval{" "}
          <span className="text-gray-300">
            (
            {formatAmount(
              toleranceData?.data?.annualApprovalLimits?.agm || "0"
            )}
            )
          </span>
        </div>
      ),
      accessorFn: (row: any) => formatAmount(row?.agmApproval || "0"),
    },
  ];

  const bomColumns = [
    {
      accessorKey: "positionName",
      header: <div>Position Name</div>,
    },
    {
      accessorKey: "approvedAmt",
      meta: { align: "right" },
      header: <div>Total Approved Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmt || "0"),
    },
  ];

  const onOptionChanges = (value: string) => {
    setSelectedOption(value);
    setTimeout(() => {
      refetchToleranceData();
    }, 10);
  };

  return {
    breadcrumbData,
    communityDetail,
    currentFinanceYear,
    isLoadingTolerance,
    toleranceData,
    summaryCards,
    totalExpense,
    onSummaryCardClick,
    filter,
    columns,
    openBomDialog,
    setOpenBomDialog,
    selectedRow,
    bomColumns,
    onOptionChanges,
    selectedOption,
    isRefetchingToleranceData
  };
};

export default useToleranceReport;
